/**
 * Debug utilities for localStorage management
 * These functions can be called from browser console
 */

// Make these available globally for debugging
declare global {
    interface Window {
        clearWelfareStorage: () => void;
        checkStorageQuota: () => void;
        getStorageSize: () => void;
    }
}

export const clearWelfareStorage = (): void => {
    try {
        console.log('Clearing all welfare app storage...');

        // Clear specific items
        const itemsToClear = ['apiError', 'apiRequest', 'persist:root'];

        itemsToClear.forEach(item => {
            if (localStorage.getItem(item)) {
                console.log(`Clearing ${item} from localStorage`);
                localStorage.removeItem(item);
            }
        });

        console.log('Storage cleared successfully!');
        console.log('Please refresh the page.');
    } catch (error) {
        console.error('Error clearing storage:', error);
        console.log('Attempting full localStorage clear...');

        try {
            localStorage.clear();
            console.log('Full localStorage cleared! Please refresh the page.');
        } catch (clearError) {
            console.error('Failed to clear localStorage:', clearError);
        }
    }
};

export const checkStorageQuota = (): void => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
        navigator.storage.estimate().then(estimate => {
            const used = estimate.usage || 0;
            const quota = estimate.quota || 0;
            const percentUsed = ((used / quota) * 100).toFixed(2);

            console.log('Storage Quota Info:');
            console.log(`Used: ${(used / 1024 / 1024).toFixed(2)} MB`);
            console.log(`Quota: ${(quota / 1024 / 1024).toFixed(2)} MB`);
            console.log(`Percentage used: ${percentUsed}%`);

            if (parseFloat(percentUsed) > 80) {
                console.warn('Storage usage is high! Consider clearing some data.');
            }
        });
    } else {
        console.log('Storage estimation not supported in this browser');
    }
};

export const getStorageSize = (): void => {
    let total = 0;
    const items: { [key: string]: number } = {};

    for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
            const size = localStorage[key].length;

            items[key] = size;
            total += size;
        }
    }

    console.log('localStorage items by size:');
    Object.entries(items)
        .sort(([, a], [, b]) => b - a)
        .forEach(([key, size]) => {
            console.log(`${key}: ${(size / 1024).toFixed(2)} KB`);
        });

    console.log(`Total localStorage size: ${(total / 1024).toFixed(2)} KB`);
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
    window.clearWelfareStorage = clearWelfareStorage;
    window.checkStorageQuota = checkStorageQuota;
    window.getStorageSize = getStorageSize;
}

export default { clearWelfareStorage, checkStorageQuota, getStorageSize };
